/**
 * First-paint theming, shared by the build (static shell) and the runtime.
 *
 * The build inlines one CSS block per theme keyed by `[data-theme]`, plus a
 * default that follows `prefers-color-scheme` (SPEC §6: the first visit follows
 * the OS). A tiny inline script applies the visitor's stored choice before
 * first paint, so there is no flash of the wrong theme. After boot, the
 * ThemeService takes over and stays authoritative.
 */
// Explicit extensions: also loaded by build tooling (see src/themes/index.ts).
import { defaultThemeFor } from "../../themes/index.ts";
import type { Theme } from "../../themes/index.ts";
import { themeCssBlock } from "./flatten.ts";

/** localStorage key holding the visitor's chosen theme id. */
export const THEME_STORAGE_KEY = "kn.theme";

/** The theme choice that follows the OS color scheme (the default), rather than naming a theme. */
export const SYSTEM_THEME = "system";

export function themeBootCss(themes: readonly Theme[]): string {
  const dark = defaultThemeFor("dark");
  const light = defaultThemeFor("light");

  return [
    themeCssBlock(":root:not([data-theme])", dark),
    `@media (prefers-color-scheme: light) {\n${indent(themeCssBlock(":root:not([data-theme])", light))}\n}`,
    ...themes.map((theme) => themeCssBlock(`:root[data-theme="${theme.id}"]`, theme)),
  ].join("\n");
}

/**
 * Inline script: applies the stored theme id before first paint. System (or
 * nothing stored) leaves the OS-driven default CSS in charge. Storage may
 * throw (privacy modes).
 */
export function themeBootScript(): string {
  const key = JSON.stringify(THEME_STORAGE_KEY);
  const system = JSON.stringify(SYSTEM_THEME);
  return `try{var t=localStorage.getItem(${key});if(t&&t!==${system})document.documentElement.dataset.theme=t}catch(e){}`;
}

function indent(text: string): string {
  return text
    .split("\n")
    .map((line) => `  ${line}`)
    .join("\n");
}
